import type { CampoRegra, CondicaoRegra } from '@/api/regrasDistribuicao';
import { formatPrice } from '@/lib/formatters';

/** Os critérios das regras, na ordem em que aparecem no editor. */
export const CAMPOS: { campo: CampoRegra; rotulo: string; ajuda: string }[] = [
  { campo: 'CLIENTE', rotulo: 'Cliente', ajuda: 'O cliente do escritório é parte no processo' },
  { campo: 'POLO_DO_CLIENTE', rotulo: 'Polo do cliente', ajuda: 'O cliente é o autor ou o réu' },
  { campo: 'PARTE_NOME', rotulo: 'Parte (nome)', ajuda: 'Uma parte com este nome, como "INSS"' },
  { campo: 'PARTE_DOCUMENTO', rotulo: 'Parte (CPF/CNPJ)', ajuda: 'Uma parte com este CPF ou CNPJ' },
  { campo: 'ASSUNTO', rotulo: 'Assunto', ajuda: 'O assunto, com todos os que ficam abaixo dele' },
  { campo: 'CLASSE', rotulo: 'Classe', ajuda: 'A classe, com todas as que ficam abaixo dela' },
  { campo: 'VALOR', rotulo: 'Valor da causa', ajuda: 'Uma faixa de valor' },
  { campo: 'JUSTICA', rotulo: 'Justiça', ajuda: 'Estadual, federal, do trabalho…' },
  { campo: 'TRIBUNAL', rotulo: 'Tribunal', ajuda: 'A sigla: TJSP, TRF5, TRT19…' },
  { campo: 'UF', rotulo: 'Estado', ajuda: 'A UF do processo' },
  { campo: 'ORGAO', rotulo: 'Órgão julgador', ajuda: 'Um trecho do nome da vara ou câmara' },
  { campo: 'MUNICIPIO', rotulo: 'Município', ajuda: 'O código IBGE do município do órgão (7 dígitos)' },
  { campo: 'ORIGEM', rotulo: 'Como chegou', ajuda: 'Pela intimação, pelo cadastro ou pela importação' },
  { campo: 'DONO', rotulo: 'Chegou pela conta de', ajuda: 'O advogado por cuja OAB o processo chegou' },
  {
    campo: 'IA',
    rotulo: 'Critério da IA',
    ajuda: 'Descreva o tipo de processo; a IA lê a capa e sugere (1 crédito de IA a cada 100 processos avaliados)',
  },
];

export const ROTULO_DO_CAMPO = Object.fromEntries(CAMPOS.map((c) => [c.campo, c.rotulo])) as Record<
  CampoRegra,
  string
>;

export const JUSTICAS: { valor: string; rotulo: string }[] = [
  { valor: 'estadual', rotulo: 'Estadual' },
  { valor: 'federal', rotulo: 'Federal' },
  { valor: 'trabalho', rotulo: 'do Trabalho' },
  { valor: 'eleitoral', rotulo: 'Eleitoral' },
  { valor: 'militar_estadual', rotulo: 'Militar estadual' },
  { valor: 'militar_uniao', rotulo: 'Militar da União' },
  { valor: 'superior', rotulo: 'Tribunais superiores' },
];

export const ORIGENS: { valor: string; rotulo: string }[] = [
  { valor: 'DJEN', rotulo: 'Intimação (DJEN)' },
  { valor: 'MANUAL', rotulo: 'Cadastro manual' },
  { valor: 'PJE', rotulo: 'PJe' },
  { valor: 'DATALAKE', rotulo: 'Importação do acervo' },
  { valor: 'FAMILIA', rotulo: 'Processo relacionado' },
];

export const POLOS: { valor: string; rotulo: string }[] = [
  { valor: 'AUTOR', rotulo: 'Autor' },
  { valor: 'REU', rotulo: 'Réu' },
];

export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

const FIXOS: Partial<Record<CampoRegra, { valor: string; rotulo: string }[]>> = {
  JUSTICA: JUSTICAS,
  ORIGEM: ORIGENS,
  POLO_DO_CLIENTE: POLOS,
};

/** O nome de um valor: o rótulo guardado, o da lista fixa, ou o próprio valor. */
export function rotuloDoValor(c: CondicaoRegra, valor: string): string {
  const fixo = FIXOS[c.campo]?.find((o) => o.valor === valor)?.rotulo;
  if (fixo) return fixo;
  const guardado = c.rotulos?.[valor];
  if (c.campo === 'ASSUNTO' || c.campo === 'CLASSE') return guardado ? `${guardado} (${valor})` : valor;
  return guardado ?? valor;
}

/** Critérios cujo valor já diz o que é ("INSS", "Horas extras"); nos outros o nome do campo vai junto. */
const FALAM_POR_SI: CampoRegra[] = ['CLIENTE', 'ASSUNTO', 'CLASSE', 'PARTE_NOME'];

/** O nome que a regra ganha se o gestor não der um: "INSS, Caixa → Equipe B". Vazio sem critério ou destino. */
export function sugerirNome(condicoes: CondicaoRegra[], destino: string): string {
  const [c, ...resto] = condicoes;
  if (!c || !destino) return '';
  let quando: string;
  if (c.campo === 'VALOR') {
    quando = descreverCondicao(c);
  } else if (c.campo === 'IA') {
    const texto = (c.valores[0] ?? '').replace(/\s+/g, ' ').trim();
    quando = texto.length > 50 ? `${texto.slice(0, 50).trimEnd()}…` : texto;
  } else {
    // Sem o código da TPU: no nome ele só atrapalha.
    const nomes = c.valores.slice(0, 2).map((v) => c.rotulos?.[v] ?? rotuloDoValor(c, v));
    quando = nomes.join(', ') + (c.valores.length > 2 ? '…' : '');
    if (!FALAM_POR_SI.includes(c.campo)) quando = `${ROTULO_DO_CAMPO[c.campo]}: ${quando}`;
  }
  const mais = resto.length ? ` (+${resto.length})` : '';
  return `${quando}${mais} → ${destino}`.slice(0, 120);
}

/** "Assunto: Direito Previdenciário (195) ou Direito Civil (899)". */
export function descreverCondicao(c: CondicaoRegra): string {
  if (c.campo === 'VALOR') {
    // formatPrice recebe centavos.
    const min = c.minimo ? formatPrice(Math.round(Number(c.minimo) * 100)) : null;
    const max = c.maximo ? formatPrice(Math.round(Number(c.maximo) * 100)) : null;
    const faixa = min && max ? `de ${min} a ${max}` : min ? `a partir de ${min}` : `até ${max}`;
    return `${ROTULO_DO_CAMPO.VALOR}: ${faixa}`;
  }
  if (c.campo === 'IA') return `${ROTULO_DO_CAMPO.IA}: “${c.valores[0] ?? ''}”`;
  const polo = c.polo ? ` (${c.polo === 'AUTOR' ? 'autor' : 'réu'})` : '';
  return `${ROTULO_DO_CAMPO[c.campo]}${polo}: ${c.valores.map((v) => rotuloDoValor(c, v)).join(' ou ')}`;
}
